<template>
  <div class="main-instructors-container main-page-body">
    <HeroSection
      :title="page.title"
      :subtitle="removeTags(page.content)"
      button-title="Start Learning"
      button-link="/signup/"
      image-url="instructors.jpg"
      class-name="instructors-shadow"
      class="instructors-hero"
    />
    <Lazy :on-interaction="['click', 'touchstart']">
      <div class="instructors-members">
        <div class="container">
          <h2>
            Meet the 365 Data Science Instructors
          </h2>
          <div class="container">
            <div class="row">
              <template
                v-for="(instructor, i) in instructors"
              >
                <div
                  v-if="!instructor.hidden && instructor.name"
                  :key="i"
                  class="single-member col-3 col sm-6"
                  :class="{ 'hide-for-phone': i > 8 && showMore !== true }"
                >
                  <nuxt-link
                    class="front-cover"
                    :to="`${instructor.userSlug}/`"
                  >
                    <div class="member-image">
                      <img loading="lazy" :src="`${$config.imagesUrl}/team/thumb@360_${instructor.image}`" :alt="instructor.name">
                    </div>
                    <div class="member-desc">
                      <div class="h6">
                        {{ instructor.name }}
                      </div>
                      <div class="instructor-signature">
                        <span>{{ instructor.signature }}</span>
                        <Component :is="signatureCompany(instructor.companies.find(c => +c.id === +instructor.signatureCompany).slug)" v-if="instructor.companies.find(c => +c.id === +instructor.signatureCompany)" class="signature-company" />
                      </div>
                    <!-- <pre>{{instructor.companies}}</pre> -->
                    </div>
                  </nuxt-link>
                </div>
              </template>
              <div class="col-3 col sm-6">
                <div class="single-member join-our-team">
                  <div class="front-cover">
                    <span class="h5">Join Our Team of Data Science Instructors</span>
                    <div class="icon-wrap">
                      <IconUserPlus />
                    </div>
                    <nuxt-link
                      to="/contact-us/"
                      class="btn-primary"
                    >
                      Apply Now
                    </nuxt-link>
                  </div>
                </div>
              </div>
              <div
                class="see-more"
                :class="{ collapsed: showMore }"
                @click="showMore = !showMore"
              >
                <span
                  v-if="!showMore"
                >
                  See More <IconArrowDown />
                </span>
                <span
                  v-else
                >
                  See Less <IconArrowDown />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Lazy>
    <Lazy never>
      <div class="we-teach">
        <div class="container">
          <div class="row">
            <div class="col col-3 mb-60">
              <h2>
                Teaching Through Values
              </h2>
              <p class="body-2 mb-48">
                Our data science instructors' high-quality courses represent only part of what we stand for.
              </p>
              <ButtonArrow text="Start Your Career" type="nuxt-link" to="/signup/" />
            </div>
            <div class="offset-1 col col-8">
              <div class="row">
                <div class="col col-6">
                  <div class="single-value">
                    <h3 class="h4">
                      <IconGuidance />
                      Guidance
                    </h3>
                    <div class="body-2">
                      Our expert instructors provide timely support, advice, and encouragement throughout your learning journey.
                    </div>
                  </div>
                </div>
                <div class="col col-6">
                  <div class="single-value">
                    <h3 class="h4">
                      <IconCommunity />
                      Community
                    </h3>
                    <div class="body-2">
                      Join a global community of successful students where everyone is welcome to start learning and achieve their goals.
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col col-6">
                  <div class="single-value">
                    <h3 class="h4">
                      <IconCommitment />
                      Commitment
                    </h3>
                    <div class="body-2">
                      We are committed to excellence in our curriculum. Our program constantly expands with new courses to provide the most in-demand data science skills at no extra cost.
                    </div>
                  </div>
                </div>
                <div class="col col-6">
                  <div class="single-value">
                    <h3 class="h4">
                      <IconProgress />
                      Progress
                    </h3>
                    <div class="body-2">
                      Each data science course is designed to develop your expertise and equip you with all the tools necessary to become a confident data science professional.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Lazy>
    <Lazy never>
      <div class="companies-universities">
        <div class="container">
          <div class="row companies-row">
            <div class="col col-7">
              <div class="logos company-logos">
                <div v-for="(logo, i) in companyLogos" :key="i" class="logo">
                  <img loading="lazy" :src="require(`../../assets/svg/companies/logo-${logo}.svg`)" :alt="`${logo}-logo`">
                </div>
              </div>
            </div>
            <div class="offset-1 col col-4 desc mb-48">
              <div>
                <h2>Instructors from Blue-Chip Companies</h2>
                <p class="body-2">
                  Our 365 Data Science instructors not only teach data science but share their hands-on expertise to benefit students and show how to apply best practices, inform decisions, and provide unique perspectives. You learn from professionals who hold top positions at blue-chip companies worldwide.
                </p>
                <ButtonArrow text="Start Learning" type="nuxt-link" to="/signup/" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Lazy>
    <Lazy never>
      <div class="companies-universities universities">
        <div class="container">
          <div class="row">
            <div class="col col-4 desc mb-48">
              <div>
                <h2>Instructors with Top-Tier Education</h2>
                <p class="body-2">
                  Our data science instructors hold degrees from some of the world’s most elite universities. Their diverse qualifications contribute to the high-quality data science training you receive with our program.
                </p>
                <ButtonArrow text="Start Learning" type="nuxt-link" to="/signup/" />
              </div>
            </div>
            <div class="offset-1 col col-7">
              <div class="logos university-logos">
                <div v-for="(logo, i) in universityLogos" :key="i" class="logo">
                  <img loading="lazy" :src="require(`../../assets/svg/universities/logo-${logo}.svg`)" :alt="`${logo}-logo`">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Lazy>
    <Lazy never>
      <PreFooter
        title="Join Our Data Science Instructors Team"
        description="We’re always excited to welcome inspired experts to build upon our program and bring value to our students."
        button-title="Apply Now"
        button-link="/contact-us/"
        columns="col-8"
      />
    </Lazy>
  </div>
</template>

<script>
import Lazy from 'vue-lazy-hydration';
import global from '@itbpbg/lms-components/src/mixins/global';
import ButtonArrow from '@itbpbg/lms-components/src/components/ButtonArrow';
import HeroSection from '@itbpbg/lms-components/src/components/HeroSection';
import PreFooter from '../../components/PreFooter';
import IconUserPlus from '../../assets/svg/user-plus.svg?vue-component';
import IconGuidance from '../../assets/svg/guidance.svg?vue-component';
import IconCommitment from '../../assets/svg/commitment.svg?vue-component';
import IconCommunity from '../../assets/svg/community.svg?vue-component';
import IconProgress from '../../assets/svg/progress.svg?vue-component';
import IconArrowDown from '../../assets/svg/icon-expand.svg?vue-component';

/* eslint-disable global-require */
/* eslint-disable import/no-dynamic-require */
export default {
  components: {
    Lazy,
    ButtonArrow,
    HeroSection,
    PreFooter,
    IconUserPlus,
    IconGuidance,
    IconCommitment,
    IconCommunity,
    IconProgress,
    IconArrowDown,
  },
  mixins: [global],
  middleware({ $config, error }) {
    if ($config.platform === 'fa') {
      return error({ statusCode: 404, message: 'Page not found' });
    }
    return true;
  },
  scrollToTop: true,
  async asyncData({ $axios, route, $config }) {
    const instructors = await $axios.$get('/team/instructors');
    const page = await $axios.$get(`/pages/${route.fullPath}`);
    page.fullPath = `${$config.siteUrl}${route.fullPath}`;
    page.metaFullImageUrl = `${$config.siteUrl}/resources/${page.metaImage}`;

    page.created = page.created || new Date();
    page.updated = page.updated || new Date();

    return {
      instructors,
      page,
      selectedInstructor: -1,
    };
  },
  data: () => ({
    showMore: false,
    companyLogos: ['google', 'facebook', 'coca-cola', 'instagram', 'deloitte', 'pwc', 'experian', 'infineon', 'h'],
    universityLogos: ['cambridge', 'oxford', 'edinburgh', 'caltech', 'bocconi', 'texas'],
  }),
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
      {
        key: 'ImageObject',
        values: {
          '@id': `${this.page.fullPath}#primaryimage`,
          url: this.page.metaFullImageUrl,
          width: this.page.metaImageWidth,
          height: this.page.metaImageHeight,
          caption: this.page.title,
        },
      },
    ]);

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps(this.page),
      script: [linkedData],
    };
  },
  methods: {
    signatureCompany(slug) {
      let company = '';
      if (slug) {
        company = require(`../../assets/svg/signatures/company-${slug}.svg?vue-component`);
      }

      return company;
    },
  },
};
</script>
<style scoped lang="scss">
.instructors-hero {
  ::v-deep {
    .container {
      .subtitle {
        margin-bottom: var(--spacing-48);
      }
    }
  }
}

.instructors-members {
  background-color: var(--main-dark);
  color: var(--main-light);
  padding: var(--spacing-100) 0 var(--spacing-120);

  h2 {
    text-align: center;
    margin-top: 0;
    margin-bottom: var(--spacing-36);
  }

  .see-more {
    display: none;

    &.collapsed {
      svg {
        transform: rotate(180deg);
      }

      &:after {
        background: transparent;
      }
    }
  }

  .single-member {
    box-shadow: 0 16px 30px rgba(0, 12, 31, 0.08);
    margin-bottom: 30px;

    .front-cover {
      display: block;
      border-radius: 7px;
      background-color: var(--main-dark-shade-1);
      height: 100%;
      overflow: hidden;
      color: var(--main-light);

      .member-image {
        position: relative;
        overflow: hidden;
        height: 270px;

        img {
          display: block;
          width: 100%;
          height: 100%;
          object-position: center top;
          object-fit: cover;
          transition: all var(--duration) ease-in;
        }
      }

      .member-desc {
        padding: var(--spacing-16) var(--spacing-24) var(--spacing-20);

        .h6 {
          margin-bottom: var(--spacing-8);
        }

        .instructor-signature {
          line-height: 150%;
          color: var(--main-light-shade-4);

          svg {
            width: 77px;
          }

          span {
            margin-right: var(--spacing-8);
            display: block;
          }
        }

        .signature-company {
          path[fill]:not([fill="#141F31"]):not([fill="#293343"]) {
            fill: var(--main-light-shade-4);
          }

          path[stroke] {
            stroke: var(--main-light-shade-4);
          }
        }
      }

      &:hover {
        img {
          transform: scale(1.01);
        }
      }
    }

    &.join-our-team {
      background-color: var(--main-dark-shade-2);
      border-radius: 7px;
      padding: var(--spacing-36);
      text-align: center;
      height: calc(100% - 30px);
      overflow: hidden;

      .front-cover {
        background-color: var(--main-dark-shade-2);
        height: 100%;
        min-height: 300px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
      }

      .icon-wrap {
        border-radius: 50%;
        background-color: #323B4B;
        width: 140px;
        height: 140px;
        display: flex;
        margin: auto;

        svg {
          margin: auto;
        }
      }

      .btn-primary {
        width: 100%;
      }
    }
  }

  @include bp($bp-phone) {
    padding: var(--spacing-40) 0 var(--spacing-80);

    .join-our-team {
      display: none;
    }

    h2 {
      font-size: 28px;
    }

    .single-member {
      margin: auto;
      margin-bottom: var(--spacing-24);
    }

    .hide-for-phone {
      display: none;
    }

    .see-more {
      margin: var(--spacing-16) auto;
      display: block;
      text-align: center;
      color: var(--accent-shade-1);
      font-size: 16px;
      font-weight: 600;
      width: 100%;
      position: relative;

      &:after {
        content: '';
        position: absolute;
        top: -150px;
        left: 0;
        width: 100%;
        height: 120px;
        background: linear-gradient(0deg, var(--main-dark) -12.31%, var(--main-dark) 30.39%, rgba(0, 12, 31, 0) 100%);
      }

      span {
        cursor: pointer;

        svg {
          path {
            fill: var(--accent-shade-1);
          }
        }
      }
    }
  }
}

.we-teach {
  background-color: var(--main-dark-shade-1);
  color: var(--main-light);
  padding: var(--spacing-100) 0 var(--spacing-60);

  h2,
  .h4 {
    margin: 0 0 var(--spacing-16) 0;
  }

  .mb-48 {
    margin-bottom: var(--spacing-48);
  }

  .single-value {
    margin-bottom: var(--spacing-60);
    padding-left: var(--spacing-56);
    position: relative;

    svg {
      vertical-align: top;
      margin-right: 18px;
      position: absolute;
      left: 0;
    }
  }

  .btn-arrow {
    font-size: 18px;
  }

  @include bp($bp-mobile) {
    .container .row .mb-60 {
      margin-bottom: var(--spacing-60);
    }

    .single-value {
      margin-bottom: var(--spacing-36);

      .h4 {
        font-size: 21px;
      }
    }
  }
}

.companies-universities {
  background-color: var(--main-light-shade-1);
  padding: var(--spacing-100) 0 var(--spacing-80);

  @include bp($bp-mobile) {
    padding: var(--spacing-80) 0 var(--spacing-40);

    &.universities {
      padding-bottom: var(--spacing-120);
    }

    .logos {
      max-width: 700px;
    }
  }

  .universities {
    padding: var(--spacing-80) 0 var(--spacing-120);
  }

  .logos {
    background-color: var(--main-light-shade-2);
    border-radius: 10px;
    padding: var(--spacing-44) var(--spacing-44) var(--spacing-20);
    overflow: hidden;

    .logo {
      background-color: var(--main-light);
      box-shadow: 0 16px 30px rgba(0, 12, 31, 0.08);
      border-radius: 7px;
      display: flex;
      align-items: center;
      margin: 0 var(--spacing-24) var(--spacing-24) 0;
      float: left;

      img {
        margin: auto;
        max-width: 80%;
      }
    }

    &.company-logos {
      .logo {
        max-width: 170px;
        height: 100px;
        width: calc(33.33333% - var(--spacing-24));

        &:nth-child(4) {
          margin-left: var(--spacing-24);
        }

        &:nth-child(6) {
          margin-right: 0;
        }
      }
    }

    &.university-logos {
      padding: var(--spacing-44) 71px var(--spacing-20) 71px;

      .logo {
        max-width: 240px;
        height: 110px;
        width: calc(50% - var(--spacing-24));

        &:nth-child(2n +1) {
          margin-left: 24px;
        }

        &:nth-child(3) {
          margin-left: 0;
        }

        &:nth-child(2n) {
          margin-right: 0;
        }
      }
    }
  }

  .desc {
    display: flex;
    align-items: center;

    h2 {
      margin: 0 0 var(--spacing-36);
    }

    .body-2 {
      margin: 0 0 var(--spacing-48);
    }

    @include bp($bp-mobile) {
      &.mb-48 {
        margin-bottom: var(--spacing-48);
      }

      .body-2 {
        margin-bottom: var(--spacing-36);
      }
    }
  }

  @include bp($bp-mobile) {
    .companies-row {
      flex-direction: column-reverse;
    }
  }

  @include bp($bp-phone) {
    .col-1 {
      margin-bottom: 50px;
    }

    .col-7 {
      padding: 0;
    }

    .logos {
      background-color: var(--main-light);
      padding: 0 var(--spacing-12);

      &.company-logos {
        .logo {
          margin: var(--spacing-12) var(--spacing-12) var(--spacing-12) 0;
          width: calc(50% - var(--spacing-24));

          &:nth-child(4n) {
            margin-left: 0;
            margin-right: 0;
          }

          &:nth-child(3),
          &:nth-child(7),
          &:nth-child(11) {
            margin-left: var(--spacing-24);
          }
        }
      }

      &.university-logos {
        padding: var(--spacing-48) var(--spacing-12) var(--spacing-12);

        .logo {
          margin: var(--spacing-12) var(--spacing-12) 0 0;
          width: calc(50% - var(--spacing-24));

          &:nth-child(4n +1) {
            margin-left: 0;
            margin-right: var(--spacing-12);
          }

          &:nth-child(3),
          &:nth-child(7),
          &:nth-child(11) {
            margin-left: var(--spacing-24);
          }
        }
      }
    }
  }
}
</style>
